import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import cn from 'classnames'
import { useIsMobile } from '../hooks/useMediaQuery'
import { getParamsStr } from '../hooks/useUrlParams'
import { shuffleArray } from '../utils/arrayUtils'
import { getImageUrl } from '../utils/assetUtils'
import { ButtonSelect } from './ButtonSelect'
import { ProductCategorySentence } from './ProductCategorySentence'

function ProductFinder(props) {
  const { categories = [], productsUrl } = props

  const isMobile = useIsMobile()

  const screenSaverIntervalRef = useRef(null)
  const [screenSaverCategory, setScreenSaverCategory] = useState(null)
  const [hoverCategory, setHoverCategory] = useState(null)
  const [attributeValues, setAttributeValues] = useState({})

  const [categorySelectorOpen, setCategorySelectorOpen] = useState(false)
  const [wiggleWitIt, setWiggle] = useState(false)

  const [selectedCategorySlug, setSelectedCategorySlug] = useState(null)
  const categoriesWithImages = categories.filter((c) => !!c.image)
  const selectedCategory = categories.find((c) => c.slug === selectedCategorySlug)
  const categoryFilterGroups = selectedCategory ? selectedCategory.filter_groups || [] : []

  const currentCategoryAttributeValues = categoryFilterGroups.reduce((acc, filterGroup) => {
    if (filterGroup.slug in attributeValues) return { ...acc, [filterGroup.slug]: attributeValues[filterGroup.slug] }
    return acc
  }, {})

  const paramsStr = getParamsStr({ category: selectedCategorySlug, ...currentCategoryAttributeValues })

  let activeImage = null
  if (selectedCategory) {
    if (selectedCategory.image) activeImage = selectedCategory.image
  } else if (hoverCategory) {
    if (hoverCategory.image) activeImage = hoverCategory.image
  } else if (screenSaverCategory) {
    activeImage = screenSaverCategory.image
  }

  const updateAttributeValue = useCallback((slug, value) => {
    setAttributeValues((obj) => ({ ...obj, [slug]: value }))
  }, [])

  const handleCategorySelect = useCallback((slug) => {
    const category = categories.find((c) => c.slug === slug)
    if (category && category.link) {
      window.location.href = category.link.url
      return
    }
    setSelectedCategorySlug(slug)
  }, [])

  useEffect(() => {
    const randomlySortedCategories = shuffleArray(
      categoriesWithImages.filter((category) => (isMobile ? category.full_size_image : true))
    )
    let index = 0
    const chooseNextRandom = () => {
      index = (index + 1) % randomlySortedCategories.length
      setScreenSaverCategory(randomlySortedCategories[index])
    }
    screenSaverIntervalRef.current = setInterval(chooseNextRandom, 3000)
    chooseNextRandom()
    return () => {
      clearInterval(screenSaverIntervalRef.current)
      screenSaverIntervalRef.current = null
    }
  }, [])

  // cancel autoplay on first hover
  useEffect(() => {
    if (hoverCategory && screenSaverIntervalRef.current) {
      clearInterval(screenSaverIntervalRef.current)
      screenSaverIntervalRef.current = null
      setScreenSaverCategory(null)
    }
  }, [hoverCategory])

  // wiggle button on attribute change
  useEffect(() => {
    const stopWiggleOk = () => setWiggle(false)
    setWiggle(true)
    const timeout = setTimeout(stopWiggleOk, 450)
    return () => clearTimeout(timeout)
  }, [attributeValues])

  return (
    <div
      className={cn(
        'product-finder',
        isMobile && 'background-light-100',
        'relative min-height-100vh',
        'padding-vertical-m:xs padding-vertical-xl:s-up',
        'flex flex-column'
      )}>
      {isMobile
        ? null
        : categoriesWithImages
            .filter((category) => !isMobile || category.full_size_image)
            .map((category) => (
              <div
                key={`image_${category.image.id}`}
                className="absolute cover"
                style={{
                  opacity: activeImage && activeImage.id === category.image.id ? 1 : 0,
                  transition: 'opacity 0.3s',
                  pointerEvents: 'none',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: category.full_size_image ? 'cover' : '33% auto',
                  backgroundPosition: category.full_size_image ? undefined : 'right 2% center',
                  backgroundImage: `url("${getImageUrl(category.image, {
                    preset: category.full_size_image ? (isMobile ? '640w-auto' : '1920w-auto') : '800w-auto',
                  })}")`,
                }}
              />
            ))}
      <div className="relative z-index-1 flex-1-0-auto flex flex-column justify-content-space-between">
        <div className="flex-none">
          <div className="container">
            <h6 className="display-title display-title-xxs" style={categorySelectorOpen ? { opacity: 0.4 } : undefined}>
              Our Products &amp; Services
            </h6>
          </div>
        </div>
        {!selectedCategorySlug && (
          <>
            <div className="container">
              <div className="flex flex-row:s-up gap-xl:s-up gap-xs:xs-down align-items-center:s-up">
                <div className="flex-none">
                  <h1
                    className={cn('display-title display-title-xxl')}
                    style={categorySelectorOpen ? { opacity: 0.4 } : undefined}>
                    I'm looking for
                  </h1>
                </div>
                {isMobile ? (
                  <div className="">
                    <ButtonSelect
                      emptyText="Something..."
                      wrapperClassName="no-margin-horizontal"
                      options={categories.map((cat) => ({ value: cat.slug, text: cat.short_title || cat.title }))}
                      value={selectedCategorySlug}
                      onChange={handleCategorySelect}
                      onOpenChange={(open) => setCategorySelectorOpen(open)}
                    />
                  </div>
                ) : (
                  <div className="flex-1-1-auto flex flex-column gap-y-s height-100">
                    {categories.map((category, i) => {
                      return (
                        <div key={i}>
                          <button
                            type="button"
                            onClick={() => handleCategorySelect(category.slug)}
                            onMouseOver={() => setHoverCategory(category)}
                            className={cn(
                              'button button-xxl',
                              screenSaverCategory && screenSaverCategory.id === category.id
                                ? 'button-primary background-dark border-color-dark'
                                : 'button-dark button-border'
                            )}
                            children={category.title}
                          />
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="container">
              <a
                href={productsUrl}
                className={cn('button button-s button-primary button-text')}
                style={categorySelectorOpen ? { opacity: 0.4 } : undefined}>
                View all products &amp; services
              </a>
            </div>
          </>
        )}
        {selectedCategory && (
          <>
            <div className="container">
              <div className="margin-top-s margin-bottom-m max-width-75:s-up">
                <ProductCategorySentence
                  category={selectedCategory}
                  categories={categories}
                  onCategoryChange={(slug) => {
                    const category = categories.find((c) => c.slug === slug)
                    if (category.link) {
                      window.location.href = category.link.url
                      return
                    }
                    setSelectedCategorySlug(slug)
                  }}
                  onAttributeSelectorOpenChange={(open) => setCategorySelectorOpen(open)}
                  attributeValues={attributeValues}
                  onAttributeValueChange={updateAttributeValue}
                />
              </div>
              {isMobile && activeImage && !selectedCategory.full_size_image && (
                <div className="flex justify-content-center margin-bottom-m">
                  <img src={getImageUrl(activeImage, { preset: '640w-auto' })} />
                </div>
              )}
            </div>
            <div className="container" style={categorySelectorOpen ? { opacity: 0.4 } : undefined}>
              <a
                href={`${productsUrl}${paramsStr ? `?${paramsStr}` : ''}`}
                className={cn('button button-primary button-pill button-icon', wiggleWitIt && 'wiggle')}
                onClick={() => {}}>
                <span>Show me what you've got</span>
                <svg width="24" height="24">
                  <use xlinkHref="/assets/glyphs/glyphs.svg#search"></use>
                </svg>
              </a>
              <div className="space space-m" />
              <a href={productsUrl} className="button button-s button-primary button-text">
                View all products &amp; services
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

window.ProductFinder = {
  init: (id, props) => {
    if (!props.categories) {
      console.warn('No categories fam...')
    }
    ReactDOM.render(<ProductFinder {...props} />, document.getElementById(id))
  },
}
